/**
 * Math (SPEC §7.12): equations in details and in notes.
 *
 * An equation is kept as text with its names written as references (`{f:amount|Cost}` is the
 * field `amount`, last called Cost), so a rename shows everywhere and a removed name still reads.
 * People type names ("Cost × Times a year"); `parseText` turns them into references using the
 * names in scope, `storedText` keeps them, and `displayText` shows them with today's names.
 *
 * Values flow up only: an item's equation reads its own details and what's inside it (added up,
 * or one item inside by name); a note's lines read the lines above, its item's details and what's
 * inside. Adding up counts each number once, where it was typed in. Answers are worked out on
 * read and never stored (see `withMath`).
 */
import { ancestorsOf, descendantsOf, type ResolvedTree } from './tree'
import { formatMoney, hasValue } from './fields'
import type { FieldDef, ItemSnapshot, Money } from './types'

// ---------------------------------------------------------------------------
// Values

/** money in dollars; pct as a fraction (6.125% = 0.06125); date as days since 1970-01-01. */
export type Unit = 'num' | 'money' | 'pct' | 'date'
export interface Val {
  n: number
  u: Unit
}

const DAY = 86_400_000
const isoDays = (s: string) => Math.round(Date.parse(`${s}T00:00:00Z`) / DAY)

/** A value as it reads: $719.88, $216, 6.125%, 97.5, 1,000. */
export function formatVal(v: Val): string {
  switch (v.u) {
    case 'money':
      return formatMoney({ cents: Math.round(v.n * 100), currency: 'USD' })
    case 'pct':
      return `${trimNum(v.n * 100, 4)}%`
    case 'date':
      return new Date(v.n * DAY).toISOString().slice(0, 10)
    default:
      return trimNum(v.n, 2)
  }
}

function trimNum(n: number, places: number): string {
  const r = Math.round(n * 10 ** places) / 10 ** places
  return r.toLocaleString('en-US', { maximumFractionDigits: places })
}

/** A typed value as a number: a cost, a number, "12", "$59.99", "6.125%", a tag's last part ("plus/2"). */
export function toVal(v: unknown, def?: Pick<FieldDef, 'type'>): Val | undefined {
  if (!hasValue(v)) return undefined
  if (typeof v === 'number') return Number.isFinite(v) ? { n: v, u: def?.type === 'money' ? 'money' : 'num' } : undefined
  if (typeof v === 'object' && v && 'cents' in v) return { n: (v as Money).cents / 100, u: 'money' }
  if (typeof v === 'string') {
    if (def?.type === 'date' || /^\d{4}-\d{2}-\d{2}$/.test(v.trim())) {
      const d = isoDays(v.trim())
      return Number.isFinite(d) ? { n: d, u: 'date' } : undefined
    }
    return numberText(v)
  }
  return undefined
}

/** "$1,234.50" → money, "6.125%" → pct, "1,000" → num; anything else → undefined. */
export function numberText(s: string): Val | undefined {
  const t = s.trim()
  const m = /^(-)?(\$)?\s?(\d[\d,]*(?:\.\d+)?|\.\d+)\s?(%)?$/.exec(t)
  if (!m) return undefined
  const n = Number(m[3]!.replace(/,/g, '')) * (m[1] ? -1 : 1)
  if (!Number.isFinite(n)) return undefined
  return m[4] ? { n: n / 100, u: 'pct' } : { n, u: m[2] ? 'money' : 'num' }
}

/** A value to store as a field's value (what charts, totals and the row line read). */
export function valToField(v: Val, type: string | undefined): unknown {
  if (type === 'money' || (type !== 'number' && v.u === 'money')) return { cents: Math.round(v.n * 100), currency: 'USD' }
  if (v.u === 'date') return formatVal(v)
  return v.u === 'pct' ? v.n : Math.round(v.n * 1e6) / 1e6
}

// ---------------------------------------------------------------------------
// References and the tree of an equation

/**
 * What a name refers to: a field (`f`, by key), a tag group (`t`, whose last part is a number:
 * `plus/2`), a note line (`l`, by id), the item's own date, or a detail of one item inside (`c`).
 * `name` is what it was called when written (shown if it's gone).
 */
export type Ref =
  { k: 'f'; key: string; name: string } | { k: 't'; group: string; name: string } | { k: 'l'; id: string; name: string } | { k: 'date'; name: string } | { k: 'c'; item: string; of: Ref; name: string }

export type Cmp = '>' | '<' | '≥' | '≤' | '=' | '≠'
export type Cond = { k: 'done' } | { k: 'open' } | { k: 'tag'; tag: string } | { k: 'cmp'; op: Cmp; a: Node; b: Node } | { k: 'and'; a: Cond; b: Cond }
export type Fn = 'sum' | 'count' | 'avg' | 'min' | 'max' | 'round' | 'roundup' | 'rounddown' | 'pmt' | 'daysuntil'
export type Node = { k: 'num'; v: Val } | { k: 'ref'; ref: Ref } | { k: 'neg'; a: Node } | { k: 'bin'; op: '+' | '-' | '*' | '/'; a: Node; b: Node } | { k: 'call'; fn: Fn; args: Node[]; where?: Cond }

const FN_WORDS: Array<[string, Fn]> = [
  ['round up', 'roundup'],
  ['round down', 'rounddown'],
  ['days until', 'daysuntil'],
  ['roundup', 'roundup'],
  ['rounddown', 'rounddown'],
  ['daysuntil', 'daysuntil'],
  ['average', 'avg'],
  ['sum', 'sum'],
  ['count', 'count'],
  ['avg', 'avg'],
  ['min', 'min'],
  ['max', 'max'],
  ['round', 'round'],
  ['pmt', 'pmt'],
]
const FN_TEXT: Record<Fn, string> = {
  sum: 'sum',
  count: 'count',
  avg: 'avg',
  min: 'min',
  max: 'max',
  round: 'round',
  roundup: 'round up',
  rounddown: 'round down',
  pmt: 'pmt',
  daysuntil: 'days until',
}
/** The functions, for help and suggestions. */
export const MATH_FUNCTIONS = ['sum', 'count', 'avg', 'min', 'max', 'round', 'round up', 'round down', 'pmt', 'days until'] as const
const KEYWORDS = new Set(['where', 'and', 'done', 'open'])
const AGG: ReadonlySet<Fn> = new Set(['sum', 'count', 'avg', 'min', 'max'])

/** Whether a call adds up what's inside (`sum(Cost)`, `count`, `avg(Party where done)`) rather than its arguments. */
export function isAggregate(n: Extract<Node, { k: 'call' }>): boolean {
  if (!AGG.has(n.fn)) return false
  if (n.args.length === 0) return n.fn === 'count'
  return n.args.length === 1 && n.args[0]!.k === 'ref' && (n.args[0]!.ref.k === 'f' || n.args[0]!.ref.k === 't')
}

// ---------------------------------------------------------------------------
// Reading an equation

/** A name people can use here, and what it refers to. */
export interface ScopeName {
  name: string
  ref: Ref
}

export class MathError extends Error {
  constructor(
    message: string,
    /** An unknown name, for "did you mean". */
    readonly unknown?: string,
  ) {
    super(message)
  }
}

type Tok = { t: 'num'; v: Val } | { t: 'ref'; ref: Ref } | { t: 'op'; v: string } | { t: 'fn'; fn: Fn } | { t: 'kw'; v: string } | { t: 'date'; v: Val } | { t: 'tag'; v: string }

const OPS: Array<[string, string]> = [
  ['>=', '≥'],
  ['<=', '≤'],
  ['!=', '≠'],
  ['≥', '≥'],
  ['≤', '≤'],
  ['≠', '≠'],
  ['==', '='],
  ['×', '*'],
  ['÷', '/'],
  ['−', '-'],
  ['–', '-'],
  ['+', '+'],
  ['-', '-'],
  ['*', '*'],
  ['/', '/'],
  ['(', '('],
  [')', ')'],
  [',', ','],
  ['>', '>'],
  ['<', '<'],
  ['=', '='],
]
const isWordChar = (c: string | undefined) => !!c && /[\p{L}\p{N}_’']/u.test(c)

function tokenize(src: string, names: readonly ScopeName[]): Tok[] {
  const lower = src.toLowerCase()
  // Longest names first, so "Principal + interest" wins over "Principal".
  const sorted = [...names].sort((a, b) => b.name.length - a.name.length)
  const out: Tok[] = []
  let i = 0
  const at = (s: string) => lower.startsWith(s, i) && !isWordChar(src[i + s.length]) && !isWordChar(src[i - 1])
  while (i < src.length) {
    const c = src[i]!
    if (/\s/.test(c)) {
      i++
      continue
    }
    // A reference as stored: {f:amount|Cost}
    if (c === '{') {
      const end = src.indexOf('}', i)
      if (end < 0) throw new MathError('A name is missing its }')
      out.push({ t: 'ref', ref: readRef(src.slice(i + 1, end)) })
      i = end + 1
      continue
    }
    // A name in scope (they may hold spaces and signs, like "Principal + interest", or start with a digit, like "2nd unit rent").
    const hit = sorted.find((s) => s.name && at(s.name.toLowerCase()))
    if (hit) {
      out.push({ t: 'ref', ref: hit.ref })
      i += hit.name.length
      continue
    }
    // A detail as a tag, for where: #picked/yes
    const tag = /^#([\p{L}\p{N}][\p{L}\p{N}_\-/]*)/u.exec(src.slice(i))
    if (tag) {
      out.push({ t: 'tag', v: tag[1]!.toLowerCase() })
      i += tag[0].length
      continue
    }
    const iso = /^\d{4}-\d{2}-\d{2}/.exec(src.slice(i))
    if (iso) {
      out.push({ t: 'date', v: { n: isoDays(iso[0]), u: 'date' } })
      i += iso[0].length
      continue
    }
    const num = /^(\$\s?)?(\d{1,3}(?:,\d{3})+(?:\.\d+)?|\d+(?:\.\d+)?|\.\d+)(\s?%)?/.exec(src.slice(i))
    if (num && (c === '$' || /[\d.]/.test(c))) {
      const n = Number(num[2]!.replace(/,/g, ''))
      out.push({ t: 'num', v: num[3] ? { n: n / 100, u: 'pct' } : { n, u: num[1] ? 'money' : 'num' } })
      i += num[0].length
      continue
    }
    const fn = FN_WORDS.find(([w]) => at(w))
    if (fn) {
      out.push({ t: 'fn', fn: fn[1] })
      i += fn[0].length
      continue
    }
    const kw = [...KEYWORDS].find((w) => at(w))
    if (kw) {
      out.push({ t: 'kw', v: kw })
      i += kw.length
      continue
    }
    const op = OPS.find(([s]) => src.startsWith(s, i))
    if (op) {
      out.push({ t: 'op', v: op[1] })
      i += op[0].length
      continue
    }
    // Words that aren't a name here: up to the next sign or keyword.
    let j = i
    while (j < src.length && !/[+\-−–*/×÷(),<>=≥≤≠{]/.test(src[j]!)) {
      if (j > i && /\s/.test(src[j - 1]!) && [...KEYWORDS].some((w) => lower.startsWith(w, j) && !isWordChar(src[j + w.length]))) break
      j++
    }
    const word = src.slice(i, j).trim() || src[i]!
    throw new MathError(`No “${word}” here`, word)
  }
  return out
}

const unescape = (s: string) => s.replace(/\\([|}\\])/g, '$1')
const escape = (s: string) => s.replace(/([|}\\])/g, '\\$1')

function readRef(body: string): Ref {
  // kind:id|name, or c:<item>/<kind>:<id>|name
  const bar = body.search(/(?<!\\)\|/)
  const head = bar < 0 ? body : body.slice(0, bar)
  const name = bar < 0 ? '' : unescape(body.slice(bar + 1))
  const colon = head.indexOf(':')
  const kind = colon < 0 ? head : head.slice(0, colon)
  const id = colon < 0 ? '' : head.slice(colon + 1)
  switch (kind) {
    case 'f':
      return { k: 'f', key: id, name: name || id }
    case 't':
      return { k: 't', group: id, name: name || id }
    case 'l':
      return { k: 'l', id, name: name || 'line' }
    case 'date':
      return { k: 'date', name: name || 'Date' }
    case 'c': {
      const slash = id.indexOf('/')
      if (slash < 0) throw new MathError('A name inside is incomplete')
      return { k: 'c', item: id.slice(0, slash), of: readRef(id.slice(slash + 1)), name: name || id }
    }
    default:
      throw new MathError(`Unknown kind of name: ${kind}`)
  }
}

function refText(r: Ref, withName = true): string {
  const n = withName && r.name ? `|${escape(r.name)}` : ''
  switch (r.k) {
    case 'f':
      return `f:${r.key}${n}`
    case 't':
      return `t:${r.group}${n}`
    case 'l':
      return `l:${r.id}${n}`
    case 'date':
      return `date${n}`
    case 'c':
      return `c:${r.item}/${refText(r.of, false)}${n}`
  }
}

/** Two references to the same thing. */
export function sameRef(a: Ref, b: Ref): boolean {
  return refText(a, false) === refText(b, false)
}

function parseTokens(toks: Tok[]): Node {
  let p = 0
  const peek = () => toks[p]
  const isOp = (v: string) => peek()?.t === 'op' && (peek() as { v: string }).v === v
  const take = (v: string) => {
    if (!isOp(v)) throw new MathError(v === ')' ? 'A ( is missing its )' : `Expected ${v}`)
    p++
  }
  const expr = (): Node => {
    let a = term()
    while (isOp('+') || isOp('-')) {
      const op = (toks[p++] as { v: '+' | '-' }).v
      a = { k: 'bin', op, a, b: term() }
    }
    return a
  }
  const term = (): Node => {
    let a = unary()
    while (isOp('*') || isOp('/')) {
      const op = (toks[p++] as { v: '*' | '/' }).v
      a = { k: 'bin', op, a, b: unary() }
    }
    return a
  }
  const unary = (): Node => {
    if (isOp('-')) {
      p++
      return { k: 'neg', a: unary() }
    }
    if (isOp('+')) {
      p++
      return unary()
    }
    return primary()
  }
  const primary = (): Node => {
    const t = peek()
    if (!t) throw new MathError('Something is missing at the end')
    if (t.t === 'num' || t.t === 'date') {
      p++
      return { k: 'num', v: t.v }
    }
    if (t.t === 'ref') {
      p++
      return { k: 'ref', ref: t.ref }
    }
    if (t.t === 'op' && t.v === '(') {
      p++
      const e = expr()
      take(')')
      return e
    }
    if (t.t === 'fn') {
      p++
      if (!isOp('(')) return { k: 'call', fn: t.fn, args: [unary()] }
      p++
      const args: Node[] = []
      let where: Cond | undefined
      if (!isOp(')')) {
        if (!(peek()?.t === 'kw' && (peek() as { v: string }).v === 'where')) args.push(expr())
        while (isOp(',')) {
          p++
          args.push(expr())
        }
        if (peek()?.t === 'kw' && (peek() as { v: string }).v === 'where') {
          p++
          where = cond()
        }
      }
      take(')')
      return where ? { k: 'call', fn: t.fn, args, where } : { k: 'call', fn: t.fn, args }
    }
    if (t.t === 'kw') throw new MathError(`“${t.v}” goes inside sum( … ), count( … ) and the like`)
    if (t.t === 'tag') throw new MathError(`#${t.v} goes after where: sum(Cost where #${t.v})`)
    throw new MathError(`Unexpected ${(t as { v: string }).v}`)
  }
  const cond = (): Cond => {
    let a = condItem()
    while (peek()?.t === 'kw' && (peek() as { v: string }).v === 'and') {
      p++
      a = { k: 'and', a, b: condItem() }
    }
    return a
  }
  const condItem = (): Cond => {
    const t = peek()
    if (t?.t === 'tag') {
      p++
      return { k: 'tag', tag: t.v }
    }
    if (t?.t === 'kw' && (t.v === 'done' || t.v === 'open')) {
      p++
      return { k: t.v }
    }
    const a = expr()
    const o = peek()
    if (o?.t !== 'op' || !['>', '<', '≥', '≤', '=', '≠'].includes(o.v)) throw new MathError('After where: done, open, a #detail, or a comparison like Importance ≥ 3')
    p++
    return { k: 'cmp', op: o.v as Cmp, a, b: expr() }
  }
  const node = expr()
  if (p < toks.length) {
    const t = toks[p]!
    throw new MathError(t.t === 'op' && t.v === ')' ? 'A ) has no (' : 'Something here doesn’t fit')
  }
  return node
}

/** An equation people typed, with the names in scope. Throws MathError. */
export function parseText(text: string, names: readonly ScopeName[]): Node {
  const src = text.trim().replace(/^=\s*/, '')
  if (!src) throw new MathError('Nothing to work out')
  return parseTokens(tokenize(src, names))
}

/** An equation as stored (names as references). Throws MathError. */
export function parseStored(stored: string): Node {
  return parseTokens(tokenize(stored, []))
}

const cache = new Map<string, Node | MathError>()
/** parseStored, cached (equations are read over and over). */
export function readEquation(stored: string): Node | MathError {
  let n = cache.get(stored)
  if (!n) {
    try {
      n = parseStored(stored)
    } catch (e) {
      n = e instanceof MathError ? e : new MathError(String(e))
    }
    if (cache.size > 2000) cache.clear()
    cache.set(stored, n)
  }
  return n
}

// ---------------------------------------------------------------------------
// Writing an equation

const PREC = { '+': 1, '-': 1, '*': 2, '/': 2 } as const
const OP_TEXT = { '+': '+', '-': '−', '*': '×', '/': '÷' } as const

function numText(v: Val): string {
  switch (v.u) {
    case 'money':
      return `$${trimNum(v.n, 2)}`
    case 'pct':
      return `${trimNum(v.n * 100, 6)}%`
    case 'date':
      return formatVal(v)
    default:
      return trimNum(v.n, 6)
  }
}

function write(n: Node, name: (r: Ref) => string, parent = 0): string {
  switch (n.k) {
    case 'num':
      return numText(n.v)
    case 'ref':
      return name(n.ref)
    case 'neg':
      return `−${write(n.a, name, 3)}`
    case 'bin': {
      const p = PREC[n.op]
      // Same level on the right of − or ÷ needs brackets: a − (b − c).
      const s = `${write(n.a, name, p)} ${OP_TEXT[n.op]} ${write(n.b, name, p + (n.op === '-' || n.op === '/' ? 0.5 : 0))}`
      return p < parent ? `(${s})` : s
    }
    case 'call': {
      const args = n.args.map((a) => write(a, name)).join(', ')
      const where = n.where ? `${args ? ' ' : ''}where ${condText(n.where, name)}` : ''
      return `${FN_TEXT[n.fn]}(${args}${where})`
    }
  }
}

function condText(c: Cond, name: (r: Ref) => string): string {
  switch (c.k) {
    case 'tag':
      return `#${c.tag}`
    case 'done':
    case 'open':
      return c.k
    case 'and':
      return `${condText(c.a, name)} and ${condText(c.b, name)}`
    case 'cmp':
      return `${write(c.a, name)} ${c.op} ${write(c.b, name)}`
  }
}

/** The equation as stored: names as references, `{f:amount|Cost} × {f:times_a_year|Times a year}`. */
export function storedText(n: Node): string {
  return write(n, (r) => `{${refText(r)}}`)
}

/** How a name reads now: its current name if it's still there (`label`), else the one it had. */
export type NameOf = (r: Ref) => string | undefined

/** The equation as people read it, with today's names: "Cost × Times a year". */
export function displayText(stored: string, nameOf?: NameOf): string {
  const n = readEquation(stored)
  if (n instanceof MathError) return stored.replace(/\{([^}]*)\}/g, (_, b: string) => unescape(b.split(/(?<!\\)\|/)[1] ?? b))
  return write(n, (r) => nameOf?.(r) ?? r.name)
}

/** Every reference in an equation (for renames and "what it uses"). */
export function refsOf(n: Node): Ref[] {
  const out: Ref[] = []
  const walk = (x: Node) => {
    if (x.k === 'ref') out.push(x.ref)
    else if (x.k === 'neg') walk(x.a)
    else if (x.k === 'bin') (walk(x.a), walk(x.b))
    else if (x.k === 'call') {
      x.args.forEach(walk)
      if (x.where) walkCond(x.where)
    }
  }
  const walkCond = (c: Cond) => {
    if (c.k === 'cmp') (walk(c.a), walk(c.b))
    else if (c.k === 'and') (walkCond(c.a), walkCond(c.b))
  }
  walk(n)
  return out
}

/** Whether an equation adds up what's inside (so a sum above it takes its answer as is). */
export function hasAggregate(n: Node): boolean {
  if (n.k === 'call') return isAggregate(n) || n.args.some(hasAggregate)
  if (n.k === 'bin') return hasAggregate(n.a) || hasAggregate(n.b)
  if (n.k === 'neg') return hasAggregate(n.a)
  return false
}

/** An equation with its references changed (`map` returns the same ref to leave one). */
export function remapStored(stored: string, map: (r: Ref) => Ref): string {
  const n = readEquation(stored)
  if (n instanceof MathError) return stored
  const deep = (r: Ref): Ref => {
    const m = map(r)
    return m.k === 'c' ? { ...m, of: deep(m.of) } : m
  }
  return write(n, (r) => `{${refText(deep(r))}}`)
}

/** An equation with every reference to one thing renamed (its stored name, for when it's gone). */
export function renameInStored(stored: string, match: (r: Ref) => boolean, name: string): string {
  const n = readEquation(stored)
  if (n instanceof MathError) return stored
  const map = (r: Ref): Ref => (match(r) ? { ...r, name } : r.k === 'c' ? { ...r, of: map(r.of) } : r)
  return write(n, (r) => `{${refText(map(r))}}`)
}

// ---------------------------------------------------------------------------
// Working it out

/** What an equation can read. `undefined` = no value (missing); throws MathError for a broken name. */
export interface EvalCtx {
  ref(r: Ref): Val | undefined
  /** Added up over what's inside: `of` null = count the items. */
  agg(fn: 'sum' | 'count' | 'avg' | 'min' | 'max', of: Ref | null, where: Cond | undefined, outer: EvalCtx): Val | undefined
  today: string
}

const num = (n: number): Val => ({ n, u: 'num' })

function addVals(a: Val | undefined, b: Val | undefined, sign: 1 | -1): Val | undefined {
  // Adding: a missing number counts as nothing (0).
  if (!a && !b) return undefined
  if (!a) return b && { n: sign * b.n, u: b.u === 'pct' ? 'pct' : b.u }
  if (!b) return a
  if (b.u === 'pct' && a.u !== 'pct') return { n: a.n * (1 + sign * b.n), u: a.u }
  if (a.u === 'date' && b.u === 'date') return sign === -1 ? num(a.n - b.n) : undefined
  if (a.u === 'date') return { n: a.n + sign * b.n, u: 'date' }
  const u: Unit = a.u === 'money' || b.u === 'money' ? 'money' : a.u === 'pct' && b.u === 'pct' ? 'pct' : 'num'
  return { n: a.n + sign * b.n, u }
}

function mulVals(a: Val | undefined, b: Val | undefined): Val | undefined {
  if (!a || !b) return undefined
  if (a.u === 'pct' && b.u === 'pct') return { n: a.n * b.n, u: 'pct' }
  const u: Unit = a.u === 'money' || b.u === 'money' ? 'money' : a.u === 'pct' ? (b.u === 'date' ? 'num' : b.u) : b.u === 'pct' ? (a.u === 'date' ? 'num' : a.u) : 'num'
  return { n: a.n * b.n, u }
}

function divVals(a: Val | undefined, b: Val | undefined): Val | undefined {
  if (!a || !b || b.n === 0) return undefined
  if (a.u === 'money' && b.u === 'money') return num(a.n / b.n)
  if (a.u === 'pct' && b.u === 'pct') return num(a.n / b.n)
  const u: Unit = a.u === 'money' ? 'money' : a.u === 'pct' ? 'pct' : 'num'
  return { n: a.n / b.n, u }
}

/** Work out an equation. Throws MathError for a broken name; undefined = no answer (something it needs has no value). */
export function evaluate(n: Node, ctx: EvalCtx): Val | undefined {
  switch (n.k) {
    case 'num':
      return n.v
    case 'ref':
      return ctx.ref(n.ref)
    case 'neg': {
      const a = evaluate(n.a, ctx)
      return a && { n: -a.n, u: a.u }
    }
    case 'bin': {
      const a = evaluate(n.a, ctx)
      const b = evaluate(n.b, ctx)
      if (n.op === '+') return addVals(a, b, 1)
      if (n.op === '-') return addVals(a, b, -1)
      if (n.op === '*') return mulVals(a, b)
      return divVals(a, b)
    }
    case 'call':
      return call(n, ctx)
  }
}

function call(n: Extract<Node, { k: 'call' }>, ctx: EvalCtx): Val | undefined {
  if (isAggregate(n)) {
    const of = n.args[0] && n.args[0].k === 'ref' ? n.args[0].ref : null
    return ctx.agg(n.fn as 'sum' | 'count' | 'avg' | 'min' | 'max', of, n.where, ctx)
  }
  if (n.where) throw new MathError('where needs a name to add up: sum(Cost where done)')
  const args = n.args.map((a) => evaluate(a, ctx))
  const present = args.filter((a): a is Val => !!a)
  switch (n.fn) {
    case 'sum':
      return present.reduce<Val | undefined>((s, v) => addVals(s, v, 1), undefined)
    case 'avg': {
      const s = present.reduce<Val | undefined>((t, v) => addVals(t, v, 1), undefined)
      return s && present.length ? { n: s.n / present.length, u: s.u } : undefined
    }
    case 'min':
    case 'max': {
      if (!present.length) return undefined
      const pick = present.reduce((m, v) => (n.fn === 'min' ? (v.n < m.n ? v : m) : v.n > m.n ? v : m))
      return pick
    }
    case 'count':
      return num(present.length)
    case 'round':
    case 'roundup':
    case 'rounddown': {
      const [x, places] = args
      if (!x) return undefined
      const k = 10 ** Math.max(0, Math.min(6, Math.round(places?.n ?? 0)))
      const scale = x.u === 'pct' ? 100 : 1
      const f = n.fn === 'round' ? Math.round : n.fn === 'roundup' ? Math.ceil : Math.floor
      // Rounding what shows (a percent's whole number), with a hair of tolerance for float error.
      let v = x.n * scale * k
      if (Math.abs(v - Math.round(v)) < 1e-9) v = Math.round(v)
      return { n: f(v) / k / scale, u: x.u }
    }
    case 'pmt': {
      // pmt(rate, years, amount): the monthly payment on a loan.
      const [rate, years, amount] = args
      if (!rate || !years || !amount) return undefined
      const r = (rate.u === 'pct' ? rate.n : rate.n >= 1 ? rate.n / 100 : rate.n) / 12
      const m = Math.round(years.n * 12)
      if (m <= 0) return undefined
      const p = r === 0 ? amount.n / m : (amount.n * r * (1 + r) ** m) / ((1 + r) ** m - 1)
      return { n: p, u: 'money' }
    }
    case 'daysuntil': {
      const [d] = args
      if (!d) return undefined
      if (d.u !== 'date') throw new MathError('days until needs a date: days until(Date)')
      return num(d.n - isoDays(ctx.today))
    }
  }
}

/** Whether an item passes a `where`: done, open, or a comparison read on the item (`inner`). */
export function passes(c: Cond, it: Pick<ItemSnapshot, 'done' | 'tags'>, inner: EvalCtx): boolean {
  switch (c.k) {
    case 'tag':
      return it.tags.some((t) => t === c.tag || t.startsWith(`${c.tag}/`))
    case 'done':
      return it.done === true
    case 'open':
      return it.done !== true
    case 'and':
      return passes(c.a, it, inner) && passes(c.b, it, inner)
    case 'cmp': {
      const a = evaluate(c.a, inner)
      const b = evaluate(c.b, inner)
      if (!a || !b) return c.op === '≠' ? !!a !== !!b : false
      const x = Math.round(a.n * 1e9)
      const y = Math.round(b.n * 1e9)
      return c.op === '>' ? x > y : c.op === '<' ? x < y : c.op === '≥' ? x >= y : c.op === '≤' ? x <= y : c.op === '=' ? x === y : x !== y
    }
  }
}

// ---------------------------------------------------------------------------
// Equations on items: a detail's own (`{eq}`), one for everything in a card, a note's lines

/** A detail value that's an equation: `{ eq: "<stored>" }`. */
export function isEq(v: unknown): v is { eq: string } {
  return !!v && typeof v === 'object' && typeof (v as { eq?: unknown }).eq === 'string'
}

/** A line of math in a note (SPEC §7.12): `name = …`, stored with references. */
export interface CalcLine {
  id: string
  name: string
  /** The equation as stored (or, for a plain number, just that number). */
  expr: string
}

/** How an item's detail got its answer, for the app and the agent. */
export interface CalcInfo {
  /** The equation as stored. */
  eq: string
  /** Where it's set: on the item, for everything in a card (`card` is its id), or a note line shown as a detail. */
  from: 'item' | 'card' | 'note'
  card?: string
  /** The answer as it reads ("$719.88"; "—" with no answer; "?" when broken). */
  text: string
  /** The answer, when there is one. */
  value?: Val
  /** What's wrong with it, when it can't be worked out. */
  error?: string
  /** It adds up what's inside (so a sum above takes it as is). */
  rollup: boolean
}

/** What one item's math came to. */
export interface ItemMath {
  calc: Record<string, CalcInfo>
  /** Its note's lines, by id. */
  lines?: Record<string, { value?: Val; text: string; error?: string }>
}

export interface MathIndex {
  items: ReadonlyMap<string, ItemSnapshot>
  tree: ResolvedTree
}

/**
 * Work out every equation in the household: each item's own, the ones its cards set for
 * everything inside, and its note's lines. Only items with math (or under a card that sets
 * some) get an entry.
 */
export function computeMath(idx: MathIndex, registry: ReadonlyMap<string, FieldDef>, today: string): Map<string, ItemMath> {
  return engine(idx, registry, today).math
}

type Parts = Map<string, { value: Val | undefined; items: number }>

function engine(idx: MathIndex, registry: ReadonlyMap<string, FieldDef>, today: string) {
  const out = new Map<string, ItemMath>()
  const visible = (id: string) => idx.items.has(id) && !idx.tree.inTrash.has(id) && !idx.tree.inArchive.has(id)

  // Equations set for everything in a card, by key: where they are.
  const ruleCards = new Map<string, string[]>()
  for (const it of idx.items.values()) for (const key of Object.keys(it.calcInside ?? {})) if (visible(it.id)) ruleCards.set(key, [...(ruleCards.get(key) ?? []), it.id])

  /** The equation for an item's detail, if it has one: its own, else its nearest card's (a typed value wins over a card's). */
  const source = (id: string, key: string): { eq: string; from: CalcInfo['from']; card?: string } | null => {
    const it = idx.items.get(id)
    if (!it) return null
    const own = it.props[key]
    if (isEq(own)) return { eq: own.eq, from: /^\{l:[^}]*\}$/.test(own.eq.trim()) ? 'note' : 'item' }
    if (hasValue(own)) return null
    const cards = ruleCards.get(key)
    if (!cards) return null
    for (const a of ancestorsOf(idx.tree, id)) {
      const rule = idx.items.get(a)?.calcInside?.[key]
      if (rule) return { eq: rule, from: 'card', card: a }
    }
    return null
  }

  const values = new Map<string, Val | undefined | MathError>()
  const busy = new Set<string>()
  const lineCache = new Map<string, Map<string, Val | undefined | MathError>>()

  /** A detail's value on an item: typed, or worked out. */
  const detail = (id: string, key: string): Val | undefined => {
    const k = `${id}\u0000${key}`
    if (values.has(k)) {
      const v = values.get(k)
      if (v instanceof MathError) throw v
      return v
    }
    const it = idx.items.get(id)
    if (!it) return undefined
    const src = source(id, key)
    if (!src) {
      const v = toVal(it.props[key], registry.get(key))
      values.set(k, v)
      return v
    }
    if (busy.has(k)) throw new MathError('It uses itself')
    busy.add(k)
    try {
      const n = readEquation(src.eq)
      if (n instanceof MathError) throw n
      const v = evaluate(n, ctxFor(id))
      values.set(k, v)
      return v
    } catch (e) {
      const err = e instanceof MathError ? e : new MathError(String(e))
      values.set(k, err)
      throw err
    } finally {
      busy.delete(k)
    }
  }

  /** A tag group's value on an item: the number its tag ends in (`plus/2` → 2). */
  const tagValue = (id: string, group: string): Val | undefined => {
    const it = idx.items.get(id)
    const t = it?.tags.find((x) => x.startsWith(`${group}/`))
    return t ? numberText(t.slice(group.length + 1).replace(/-/g, '.')) : undefined
  }

  const lines = (id: string): Map<string, Val | undefined | MathError> => {
    let m = lineCache.get(id)
    if (m) return m
    m = new Map()
    lineCache.set(id, m)
    const it = idx.items.get(id)
    const ctx = ctxFor(id, m)
    for (const l of it?.calcLines ?? []) {
      try {
        const n = readEquation(l.expr)
        if (n instanceof MathError) throw n
        m.set(l.id, evaluate(n, ctx))
      } catch (e) {
        m.set(l.id, e instanceof MathError ? e : new MathError(String(e)))
      }
    }
    return m
  }

  /** Whether an item's value for a name adds up what's inside it (then a sum above takes it as is). */
  const isRollup = (id: string, r: Ref): boolean => {
    if (r.k !== 'f') return false
    const src = source(id, r.key)
    if (!src) return false
    const n = readEquation(src.eq)
    return !(n instanceof MathError) && hasAggregate(n)
  }

  function ctxFor(id: string, lineVals?: Map<string, Val | undefined | MathError>): EvalCtx {
    const self: EvalCtx = {
      today,
      ref(r) {
        switch (r.k) {
          case 'f':
            return detail(id, r.key)
          case 't':
            return tagValue(id, r.group)
          case 'date': {
            const d = idx.items.get(id)?.date
            return d ? { n: isoDays(d), u: 'date' } : undefined
          }
          case 'l': {
            // A note's lines read the lines above them (as they're worked out, in order).
            const m = lineVals ?? lines(id)
            if (!m.has(r.id)) {
              if (lineVals && (idx.items.get(id)?.calcLines ?? []).some((l) => l.id === r.id)) throw new MathError(`${r.name} is further down`)
              throw new MathError(`No “${r.name}” here`, r.name)
            }
            const v = m.get(r.id)
            if (v instanceof MathError) throw new MathError(`${r.name} can’t be worked out`)
            return v
          }
          case 'c': {
            // One item inside, by name: up only.
            if (!visible(r.item) || !ancestorsOf(idx.tree, r.item).includes(id)) throw new MathError(`“${r.name}” isn’t inside this`, r.name)
            return ctxFor(r.item).ref(r.of)
          }
        }
      },
      agg(fn, of, where, outer) {
        return aggregate(id, fn, of, where, outer).value
      },
    }
    return self
  }

  /** Inside `id`: each value counted once, where it was typed in (a total inside is taken as is). */
  function aggregate(id: string, fn: 'sum' | 'count' | 'avg' | 'min' | 'max', of: Ref | null, where: Cond | undefined, outer: EvalCtx): { value: Val | undefined; parts: Parts } {
    const parts: Parts = new Map()
    let total: Val | undefined
    let n = 0
    let pick: Val | undefined
    const top = (d: string) => {
      // The item directly inside `id` that `d` is in.
      let x = d
      while (idx.tree.parentOf.get(x) && idx.tree.parentOf.get(x) !== id) x = idx.tree.parentOf.get(x)!
      return x
    }
    const visit = (d: string) => {
      if (!visible(d)) return
      const it = idx.items.get(d)!
      // A `where` reads the item's own details; names from the note (lines) read the note's.
      const inner: EvalCtx = {
        today,
        ref: (r) => (r.k === 'l' ? outer.ref(r) : ctxFor(d).ref(r)),
        agg: (f, o, w, out) => ctxFor(d).agg(f, o, w, out),
      }
      const ok = !where || passes(where, it, inner)
      let v: Val | undefined
      if (of) v = of.k === 't' ? tagValue(d, of.group) : of.k === 'f' ? detail(d, of.key) : undefined
      if (ok && (of ? v !== undefined : true)) {
        const p = top(d)
        const part = parts.get(p) ?? { value: undefined, items: 0 }
        part.items++
        if (v) part.value = addVals(part.value, v, 1)
        parts.set(p, part)
        n++
        if (v) {
          total = addVals(total, v, 1)
          if (!pick || (fn === 'min' ? v.n < pick.n : v.n > pick.n)) pick = v
        }
      }
      // A total inside of the same thing is taken as is, not counted again (unless a `where` picks items).
      if (of && !where && v !== undefined && isRollup(d, of)) return
      for (const c of idx.tree.childrenOf.get(d) ?? []) visit(c)
    }
    for (const c of idx.tree.childrenOf.get(id) ?? []) visit(c)
    let value: Val | undefined
    if (fn === 'count') value = num(n)
    else if (fn === 'sum') value = total
    else if (fn === 'avg') value = total && n ? { n: total.n / n, u: total.u } : undefined
    else value = pick
    return { value, parts }
  }

  const describe = (id: string, key: string, src: NonNullable<ReturnType<typeof source>>): CalcInfo => {
    const n = readEquation(src.eq)
    const rollup = !(n instanceof MathError) && hasAggregate(n)
    const base = { eq: src.eq, from: src.from, ...(src.card ? { card: src.card } : {}), rollup }
    try {
      const v = detail(id, key)
      return v ? { ...base, text: formatVal(v), value: v } : { ...base, text: '—' }
    } catch (e) {
      return { ...base, text: '?', error: e instanceof MathError ? e.message : String(e) }
    }
  }

  for (const it of idx.items.values()) {
    if (!visible(it.id)) continue
    const keys = new Set<string>()
    for (const [k, v] of Object.entries(it.props)) if (isEq(v)) keys.add(k)
    if (ruleCards.size) {
      const anc = new Set(ancestorsOf(idx.tree, it.id))
      for (const [key, cards] of ruleCards) if (cards.some((c) => anc.has(c))) keys.add(key)
    }
    const calc: Record<string, CalcInfo> = {}
    for (const k of keys) {
      const src = source(it.id, k)
      if (src) calc[k] = describe(it.id, k, src)
    }
    let lineOut: ItemMath['lines']
    if (it.calcLines?.length) {
      lineOut = {}
      for (const [lid, v] of lines(it.id)) lineOut[lid] = v instanceof MathError ? { text: '?', error: v.message } : v ? { value: v, text: formatVal(v) } : { text: '—' }
    }
    if (Object.keys(calc).length || lineOut) out.set(it.id, lineOut ? { calc, lines: lineOut } : { calc })
  }
  return { math: out, aggregate, isRollup }
}

/**
 * The items with their math worked in: a worked-out detail reads like a typed one (so the row
 * line, totals, charts and the agent see the answer), with `calc` saying how it was worked out.
 * Nothing here is ever written back; answers aren't stored.
 */
export function withMath<T extends MathIndex>(idx: T, registry: ReadonlyMap<string, FieldDef>, today: string, math = computeMath(idx, registry, today)): T & { math: Map<string, ItemMath> } {
  if (!math.size) return { ...idx, math }
  const items = new Map(idx.items)
  for (const [id, m] of math) {
    const it = items.get(id)
    if (it) items.set(id, overlayMath(it, m, registry))
  }
  return { ...idx, items, math }
}

/** One item with its math worked in (see withMath); the item itself when it has none. */
export function overlayMath(it: ItemSnapshot, m: ItemMath | undefined, registry: ReadonlyMap<string, FieldDef>): ItemSnapshot {
  if (!m || !Object.keys(m.calc).length) return it
  const props = { ...it.props }
  for (const [k, c] of Object.entries(m.calc)) {
    if (c.value) props[k] = valToField(c.value, registry.get(k)?.type)
    else delete props[k]
  }
  return { ...it, props, calc: m.calc }
}

/**
 * An equation tried on item `id`'s detail `key` before it's saved: on the item itself, or for
 * everything in `card` (the answer is still `id`'s). `idx`: the items as stored (not worked in).
 */
export function tryOn(idx: MathIndex, registry: ReadonlyMap<string, FieldDef>, today: string, id: string, key: string, stored: string, card?: string): CalcInfo | undefined {
  const items = new Map(idx.items)
  const it = items.get(id)
  if (!it) return undefined
  if (card) {
    const c = items.get(card)
    if (c) items.set(card, { ...c, calcInside: { ...(c.calcInside ?? {}), [key]: stored } })
    if (isEq(it.props[key])) {
      const props = { ...it.props }
      delete props[key]
      items.set(id, { ...it, props })
    }
  } else items.set(id, { ...it, props: { ...it.props, [key]: { eq: stored } } })
  return computeMath({ items, tree: idx.tree }, registry, today).get(id)?.calc[key]
}

/** What kind of detail holds an equation's answers: money, or a number. */
export function answerKind(v: Val | undefined, stored = ''): 'money' | 'number' {
  return v ? (v.u === 'money' ? 'money' : 'number') : stored.includes('$') ? 'money' : 'number'
}

// ---------------------------------------------------------------------------
// What an answer is made of

export interface MadeOfPart {
  /** The item directly inside. */
  id: string
  value?: Val
  text: string
  /** How many items inside it counted. */
  items: number
  /** It has its own total of this (taken as is). */
  total: boolean
}

/**
 * The parts of an item's added-up detail, by what's directly inside it: "Venues $23,231 ·
 * Apparel (4 items) $1,402.99". Empty when the equation doesn't add anything up.
 */
export function madeOf(idx: MathIndex, registry: ReadonlyMap<string, FieldDef>, today: string, id: string, stored: string): MadeOfPart[] {
  const n = readEquation(stored)
  if (n instanceof MathError) return []
  let agg: Extract<Node, { k: 'call' }> | null = null
  const find = (x: Node) => {
    if (agg) return
    if (x.k === 'call' && isAggregate(x)) agg = x
    else if (x.k === 'call') x.args.forEach(find)
    else if (x.k === 'bin') (find(x.a), find(x.b))
    else if (x.k === 'neg') find(x.a)
  }
  find(n)
  if (!agg) return []
  const a = agg as Extract<Node, { k: 'call' }>
  const e = engine(idx, registry, today)
  const of = a.args[0] && a.args[0].k === 'ref' ? a.args[0].ref : null
  // Names from the note (`where Importance ≥ Importance at least`) read the item's lines.
  const outer: EvalCtx = { today, ref: (r) => (r.k === 'l' ? e.math.get(id)?.lines?.[r.id]?.value : undefined), agg: () => undefined }
  let parts: Parts
  try {
    parts = e.aggregate(id, a.fn as 'sum', of, a.where, outer).parts
  } catch {
    return []
  }
  return [...parts]
    .map(([pid, p]) => {
      const total = !!of && !a.where && e.isRollup(pid, of)
      return { id: pid, ...(p.value ? { value: p.value } : {}), text: a.fn === 'count' ? String(p.items) : p.value ? formatVal(p.value) : '—', items: p.items, total }
    })
    .sort((x, y) => (y.value?.n ?? 0) - (x.value?.n ?? 0))
}

// ---------------------------------------------------------------------------
// Names in scope

/** A name as people read it now, for a reference (`label` of a field, a tag group's words, a line's name, "Guest List.Expected"). */
export function nameOfRef(r: Ref, registry: ReadonlyMap<string, FieldDef>, idx?: MathIndex, lines?: readonly CalcLine[], labelOf?: (key: string, fallback: string) => string): string | undefined {
  switch (r.k) {
    case 'f': {
      const d = registry.get(r.key)
      return d ? (labelOf?.(r.key, d.label) ?? d.label) : undefined
    }
    case 't':
      return words(r.group)
    case 'l':
      return lines?.find((l) => l.id === r.id)?.name
    case 'date':
      return 'Date'
    case 'c': {
      const t = idx?.items.get(r.item)?.title
      const of = nameOfRef(r.of, registry, idx, lines, labelOf)
      return t !== undefined && of ? `${t.trim()}.${of}` : undefined
    }
  }
}

/** What the common details are called in any + list (core COMMON_DETAILS), for fields kept under other names. */
const COMMON_NAMES: Record<string, string> = { amount: 'Cost', url: 'Link' }

const words = (group: string) => {
  const s = group.replace(/[-_]+/g, ' ')
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/**
 * The names an equation on an item can use: the details in use on it and inside it (fields, and
 * tag groups whose values are numbers), its Date, and each item inside by name with its details
 * ("Guest List.Expected"). `lines`: a note's lines above, first.
 */
export function scopeNames(idx: MathIndex, registry: ReadonlyMap<string, FieldDef>, id: string | null, lines: readonly CalcLine[] = []): ScopeName[] {
  const out: ScopeName[] = []
  const seen = new Set<string>()
  const add = (name: string, ref: Ref) => {
    const k = name.toLowerCase()
    if (!name.trim() || seen.has(k)) return
    seen.add(k)
    out.push({ name, ref })
  }
  for (const l of lines) add(l.name, { k: 'l', id: l.id, name: l.name })
  // Every field (they're shared), and the tag groups in use with numbers for values.
  for (const d of registry.values()) {
    if (d.key === 'view') continue
    add(d.label, { k: 'f', key: d.key, name: d.label })
    if (d.key !== d.label.toLowerCase()) add(d.key.replace(/_/g, ' '), { k: 'f', key: d.key, name: d.label })
    // The names every + list offers for them (as details read them): Cost is the amount, Link the url.
    const common = COMMON_NAMES[d.key]
    if (common) add(common, { k: 'f', key: d.key, name: d.label })
  }
  const groups = new Set<string>()
  const near = id ? [id, ...descendantsOf(idx.tree, id)] : [...idx.items.keys()]
  for (const x of near)
    for (const t of idx.items.get(x)?.tags ?? []) {
      const slash = t.lastIndexOf('/')
      if (slash > 0 && numberText(t.slice(slash + 1).replace(/-/g, '.'))) groups.add(t.slice(0, slash))
    }
  for (const g of groups) add(words(g), { k: 't', group: g, name: words(g) })
  add('Date', { k: 'date', name: 'Date' })
  if (id) {
    // Items inside, by name: "Guest List.Expected" (their details with values, worked out or typed).
    for (const c of descendantsOf(idx.tree, id)) {
      const it = idx.items.get(c)
      const title = it?.title.trim()
      if (!it || !title || title.length > 60) continue
      for (const [k, v] of Object.entries(it.props)) {
        const d = registry.get(k)
        if (!d || k === 'view' || !(isEq(v) || toVal(v, d))) continue
        const name = `${title}.${d.label}`
        add(name, { k: 'c', item: c, of: { k: 'f', key: k, name: d.label }, name })
      }
    }
  }
  return out
}

/** Names close to an unknown one, for "did you mean". */
export function closeNames(word: string, names: readonly ScopeName[], max = 3): string[] {
  const w = word.toLowerCase().replace(/[^a-z0-9]/g, '')
  if (!w) return []
  const dist = (a: string, b: string) => {
    const m = a.length
    const n = b.length
    const d = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)] as number[])
    for (let j = 1; j <= n; j++) d[0]![j] = j
    for (let i = 1; i <= m; i++)
      for (let j = 1; j <= n; j++) {
        d[i]![j] = Math.min(d[i - 1]![j]! + 1, d[i]![j - 1]! + 1, d[i - 1]![j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1))
        // Two letters swapped counts as one slip ("cots" for "cost").
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i]![j] = Math.min(d[i]![j]!, d[i - 2]![j - 2]! + 1)
      }
    return d[m]![n]!
  }
  return names
    .map((s) => ({ s: s.name, d: dist(w, s.name.toLowerCase().replace(/[^a-z0-9]/g, '')) }))
    .filter((x) => x.d <= Math.max(1, Math.floor(w.length / 3)) || x.s.toLowerCase().startsWith(word.toLowerCase()))
    .sort((a, b) => a.d - b.d)
    .slice(0, max)
    .map((x) => x.s)
}

// ---------------------------------------------------------------------------
// Totals in a tab (and its groups): each number counted once

/**
 * A tab's total of a detail over the items it shows: what's open, done, or everything. An item
 * whose value adds up what's inside it isn't counted along with what's inside it.
 */
export function totalOf(idx: MathIndex & { items: ReadonlyMap<string, ItemSnapshot> }, ids: readonly string[], key: string, adds: 'all' | 'open' | 'done' = 'all'): Val | undefined {
  const shown = new Set(ids)
  let total: Val | undefined
  for (const id of ids) {
    const it = idx.items.get(id)
    if (!it) continue
    if (adds === 'done' ? it.done !== true : adds === 'open' ? it.done === true : false) continue
    // Inside something shown that already adds this up: counted there.
    if (ancestorsOf(idx.tree, id).some((a) => shown.has(a) && idx.items.get(a)?.calc?.[key]?.rollup)) continue
    const v = toVal(it.props[key])
    if (v) total = addVals(total, v, 1)
  }
  return total
}
